/**
 * PromptModal.tsx
 * Modale de saisie de texte réutilisable (remplace window.prompt()).
 * Construite sur ui/Modal : titre, libellé, valeur initiale, validation Enter,
 * annulation Escape, état de chargement pendant la confirmation async.
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../../../ui/Modal';

export interface PromptModalProps {
  isOpen: boolean;
  title: string;
  label?: string;
  initialValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Interdit la confirmation si la valeur (trim) est vide. */
  required?: boolean;
  onConfirm: (value: string) => void | Promise<void>;
  onClose: () => void;
}

export function PromptModal({
  isOpen,
  title,
  label,
  initialValue = '',
  placeholder,
  confirmLabel = 'Valider',
  cancelLabel = 'Annuler',
  required = true,
  onConfirm,
  onClose,
}: PromptModalProps): React.ReactElement | null {
  const [value, setValue] = useState(initialValue);
  const [loading, setLoading] = useState(false);

  // Réinitialiser la valeur à chaque ouverture.
  useEffect(() => {
    if (isOpen) setValue(initialValue);
  }, [isOpen, initialValue]);

  const trimmed = value.trim();
  const canConfirm = !required || trimmed.length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      setLoading(true);
      await onConfirm(trimmed);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={loading ? () => {} : onClose} title={title} size="small">
      {label && (
        <label
          htmlFor="prompt-modal-input"
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 8,
            color: 'var(--color-text-secondary, #c9c9d4)',
          }}
        >
          {label}
        </label>
      )}
      <input
        id="prompt-modal-input"
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={loading}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleConfirm();
          }
        }}
        style={{
          width: '100%',
          padding: '10px 12px',
          borderRadius: 8,
          border: '1px solid var(--color-border, rgba(0,0,0,0.15))',
          background: 'var(--color-bg-input, #fff)',
          color: 'var(--color-text-primary, #111)',
          fontSize: 14,
          boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: '1px solid var(--color-border, rgba(0,0,0,0.15))',
            background: 'transparent',
            color: 'var(--color-text-primary, #111)',
            fontSize: 13,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
          }}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading || !canConfirm}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--color-primary, #7C3AED)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: loading || !canConfirm ? 'not-allowed' : 'pointer',
            opacity: loading || !canConfirm ? 0.6 : 1,
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
